import React from "react";
import { useParams } from "react-router-dom";
import { FadeUp, Stagger, StaggerItem } from "@/components/Reveal";
import { useLanguage } from "@/i18n/LanguageContext";
import {
 Building2, Server, Copyright, Camera, ShieldAlert, Lock, Cookie, Scale, Mail
} from "lucide-react";

// -----------------------------------------------------------------------------
// Mentions légales, refonte 2026
// Structure sectionnée, trilingue FR / EN / PT, avec H2, icônes discrètes et
// espaces cohérents. Aucune information juridique inventée : les emplacements
// à compléter ultérieurement sont explicitement marqués « à compléter ».
// -----------------------------------------------------------------------------

const MENTIONS_CONTENT = {
 fr: {
 label: "Légal",
 title: "Mentions légales",
 updatedAt: "Mise à jour : 2026",
 intro:
 "NANDA SÁ est une entreprise multisectorielle indépendante spécialisée dans les solutions techniques, énergétiques, industrielles, le commerce international, le développement de projets et l'accompagnement des entreprises en Afrique de l'Ouest.",
 sections: [
 {
 icon: Building2,
 title: "Éditeur du site",
 blocks: [
 { kind: "kv", rows: [
 ["Raison", "NANDA SÁ"],
 ["Nature", "Entreprise multisectorielle"],
 ["Présence", "Guinée-Bissau · depuis 2023"],
 ["Directrice de la publication", "Kadidia DIOP"],
 ["Adresse électronique", "hello@nanda-sa.com"],
 ["Nom de domaine", "www.nanda-sa.com"],
 ]},
 { kind: "note", text: "Les informations juridiques complémentaires (numéros d'immatriculation, adresses postales complètes, numéros fiscaux) seront ajoutées ici selon les juridictions concernées." },
 ],
 },
 {
 icon: Server,
 title: "Hébergement",
 blocks: [
 { kind: "p", text: "Le site www.nanda-sa.com est hébergé auprès d'un prestataire d'hébergement professionnel." },
 { kind: "note", text: "Le nom et l'adresse de l'hébergeur peuvent être ajoutés ici sur demande." },
 ],
 },
 {
 icon: Copyright,
 title: "Propriété intellectuelle",
 blocks: [
 { kind: "p", text: "L'ensemble du contenu publié sur le site www.nanda-sa.com, notamment les textes, photographies, illustrations, logos, documents, catalogues et éléments graphiques, est protégé par les dispositions relatives à la propriété intellectuelle." },
 { kind: "p", text: "Toute reproduction, représentation, adaptation ou diffusion, totale ou partielle, sans autorisation préalable écrite de NANDA SÁ est interdite." },
 { kind: "p", text: "Les marques citées sur le site demeurent la propriété de leurs titulaires respectifs." },
 ],
 },
 {
 icon: Camera,
 title: "Photographies",
 blocks: [
 { kind: "p", text: "Les photographies présentes sur ce site sont soit :" },
 { kind: "ul", items: [
 "des réalisations de NANDA SÁ ;",
 "des photographies dont NANDA SÁ dispose des droits d'utilisation ;",
 "des illustrations utilisées conformément aux licences applicables.",
 ]},
 { kind: "p", text: "Toute reproduction est interdite sans autorisation." },
 ],
 },
 {
 icon: ShieldAlert,
 title: "Limitation de responsabilité",
 blocks: [
 { kind: "p", text: "Les informations publiées sur ce site sont fournies à titre indicatif." },
 { kind: "p", text: "NANDA SÁ s'efforce d'assurer leur exactitude mais ne peut garantir l'absence totale d'erreurs ou d'omissions." },
 { kind: "p", text: "Les caractéristiques techniques et la disponibilité des produits et services peuvent évoluer sans préavis." },
 ],
 },
 {
 icon: Lock,
 title: "Protection des données personnelles",
 blocks: [
 { kind: "ul", items: [
 "Les données collectées via les formulaires servent uniquement au traitement des demandes.",
 "Aucune donnée n'est revendue à des tiers.",
 "Les informations sont utilisées uniquement dans le cadre des activités de NANDA SÁ.",
 "Les utilisateurs peuvent demander l'accès, la rectification ou la suppression de leurs données en écrivant à hello@nanda-sa.com.",
 ]},
 ],
 },
 {
 icon: Cookie,
 title: "Cookies",
 blocks: [
 { kind: "p", text: "Le site peut utiliser des cookies nécessaires à son fonctionnement, à la mesure d'audience et à l'amélioration de l'expérience utilisateur." },
 { kind: "p", text: "L'utilisateur peut gérer ses préférences via son navigateur." },
 ],
 },
 {
 icon: Scale,
 title: "Droit applicable",
 blocks: [
 { kind: "p", text: "Les présentes mentions légales sont régies par le droit applicable selon la juridiction compétente liée à l'entité NANDA SÁ concernée." },
 ],
 },
 {
 icon: Mail,
 title: "Contact",
 blocks: [
 { kind: "p", text: "Pour toute question concernant le site internet ou son contenu :" },
 { kind: "kv", rows: [
 ["Courriel", "hello@nanda-sa.com"],
 ["Site", "www.nanda-sa.com"],
 ]},
 ],
 },
 ],
 },
 en: {
 label: "Legal",
 title: "Legal notice",
 updatedAt: "Updated: 2026",
 intro:
 "NANDA SÁ is an independent multisector company specialised in technical, energy and industrial solutions, international trade, project development and business support in West Africa.",
 sections: [
 {
 icon: Building2,
 title: "Publisher",
 blocks: [
 { kind: "kv", rows: [
 ["Name", "NANDA SÁ"],
 ["Nature", "Multisector company"],
 ["Presence", "Guinea-Bissau · since 2023"],
 ["Director of publication", "Kadidia DIOP"],
 ["E-mail", "hello@nanda-sa.com"],
 ["Domain name", "www.nanda-sa.com"],
 ]},
 { kind: "note", text: "Additional legal information (registration numbers, full postal addresses, tax numbers) will be added here in line with the relevant jurisdictions." },
 ],
 },
 {
 icon: Server,
 title: "Hosting",
 blocks: [
 { kind: "p", text: "The website www.nanda-sa.com is hosted by a professional hosting provider." },
 { kind: "note", text: "The name and address of the hosting provider can be added here on request." },
 ],
 },
 {
 icon: Copyright,
 title: "Intellectual property",
 blocks: [
 { kind: "p", text: "All content published on the website www.nanda-sa.com, in particular texts, photographs, illustrations, logos, documents, catalogues and graphic elements, is protected by intellectual property laws." },
 { kind: "p", text: "Any reproduction, representation, adaptation or distribution, in whole or in part, without the prior written authorisation of NANDA SÁ is prohibited." },
 { kind: "p", text: "The trademarks mentioned on the site remain the property of their respective owners." },
 ],
 },
 {
 icon: Camera,
 title: "Photographs",
 blocks: [
 { kind: "p", text: "The photographs published on this site are either:" },
 { kind: "ul", items: [
 "achievements of NANDA SÁ;",
 "photographs for which NANDA SÁ holds the usage rights;",
 "illustrations used in accordance with the applicable licences.",
 ]},
 { kind: "p", text: "Any reproduction is prohibited without authorisation." },
 ],
 },
 {
 icon: ShieldAlert,
 title: "Limitation of liability",
 blocks: [
 { kind: "p", text: "The information published on this site is provided for information purposes only." },
 { kind: "p", text: "NANDA SÁ strives to ensure its accuracy but cannot guarantee the total absence of errors or omissions." },
 { kind: "p", text: "Technical characteristics and product or service availability may change without notice." },
 ],
 },
 {
 icon: Lock,
 title: "Personal data protection",
 blocks: [
 { kind: "ul", items: [
 "Data collected via the forms is used solely to process the requests.",
 "No data is sold to third parties.",
 "Information is used exclusively within the scope of NANDA SÁ's activities.",
 "Users may request access, rectification or deletion of their data by writing to hello@nanda-sa.com.",
 ]},
 ],
 },
 {
 icon: Cookie,
 title: "Cookies",
 blocks: [
 { kind: "p", text: "The site may use cookies necessary for its operation, for audience measurement and to improve the user experience." },
 { kind: "p", text: "Users can manage their preferences through their browser." },
 ],
 },
 {
 icon: Scale,
 title: "Applicable law",
 blocks: [
 { kind: "p", text: "This legal notice is governed by the law applicable to the relevant jurisdiction of the NANDA SÁ entity concerned." },
 ],
 },
 {
 icon: Mail,
 title: "Contact",
 blocks: [
 { kind: "p", text: "For any question regarding the website or its content:" },
 { kind: "kv", rows: [
 ["E-mail", "hello@nanda-sa.com"],
 ["Website", "www.nanda-sa.com"],
 ]},
 ],
 },
 ],
 },
 pt: {
 label: "Legal",
 title: "Menções legais",
 updatedAt: "Actualizado: 2026",
 intro:
 "A NANDA SÁ é uma empresa multisectorial independente especializada em soluções técnicas, energéticas e industriais, comércio internacional, desenvolvimento de projectos e acompanhamento de empresas na África Ocidental.",
 sections: [
 {
 icon: Building2,
 title: "Editor do site",
 blocks: [
 { kind: "kv", rows: [
 ["Nome", "NANDA SÁ"],
 ["Natureza", "Empresa multisectorial"],
 ["Presença", "Guiné-Bissau · desde 2023"],
 ["Directora de publicação", "Kadidia DIOP"],
 ["Endereço electrónico", "hello@nanda-sa.com"],
 ["Nome de domínio", "www.nanda-sa.com"],
 ]},
 { kind: "note", text: "As informações jurídicas complementares (números de matrícula, moradas completas, números fiscais) serão acrescentadas aqui de acordo com as jurisdições em causa." },
 ],
 },
 {
 icon: Server,
 title: "Alojamento",
 blocks: [
 { kind: "p", text: "O site www.nanda-sa.com está alojado num prestador de alojamento profissional." },
 { kind: "note", text: "O nome e a morada do alojador podem ser acrescentados aqui a pedido." },
 ],
 },
 {
 icon: Copyright,
 title: "Propriedade intelectual",
 blocks: [
 { kind: "p", text: "A totalidade do conteúdo publicado no site www.nanda-sa.com, nomeadamente textos, fotografias, ilustrações, logótipos, documentos, catálogos e elementos gráficos, está protegida pelas disposições relativas à propriedade intelectual." },
 { kind: "p", text: "Qualquer reprodução, representação, adaptação ou difusão, total ou parcial, sem autorização prévia escrita da NANDA SÁ é interdita." },
 { kind: "p", text: "As marcas citadas no site permanecem propriedade dos seus respectivos titulares." },
 ],
 },
 {
 icon: Camera,
 title: "Fotografias",
 blocks: [
 { kind: "p", text: "As fotografias presentes neste site são :" },
 { kind: "ul", items: [
 "realizações da NANDA SÁ;",
 "fotografias sobre as quais a NANDA SÁ detém os direitos de utilização;",
 "ilustrações utilizadas em conformidade com as licenças aplicáveis.",
 ]},
 { kind: "p", text: "Qualquer reprodução é interdita sem autorização." },
 ],
 },
 {
 icon: ShieldAlert,
 title: "Limitação de responsabilidade",
 blocks: [
 { kind: "p", text: "As informações publicadas neste site são fornecidas a título indicativo." },
 { kind: "p", text: "A NANDA SÁ esforça-se por assegurar a sua exactidão mas não pode garantir a total ausência de erros ou omissões." },
 { kind: "p", text: "As características técnicas e a disponibilidade dos produtos e serviços podem evoluir sem aviso prévio." },
 ],
 },
 {
 icon: Lock,
 title: "Protecção dos dados pessoais",
 blocks: [
 { kind: "ul", items: [
 "Os dados recolhidos através dos formulários servem unicamente para o tratamento dos pedidos.",
 "Nenhum dado é vendido a terceiros.",
 "As informações são utilizadas unicamente no âmbito das actividades da NANDA SÁ.",
 "Os utilizadores podem pedir o acesso, a rectificação ou a supressão dos seus dados escrevendo para hello@nanda-sa.com.",
 ]},
 ],
 },
 {
 icon: Cookie,
 title: "Cookies",
 blocks: [
 { kind: "p", text: "O site pode utilizar cookies necessários ao seu funcionamento, à medida de audiência e à melhoria da experiência de utilização." },
 { kind: "p", text: "O utilizador pode gerir as suas preferências através do seu navegador." },
 ],
 },
 {
 icon: Scale,
 title: "Direito aplicável",
 blocks: [
 { kind: "p", text: "As presentes menções legais são regidas pelo direito aplicável segundo a jurisdição competente ligada à entidade NANDA SÁ em causa." },
 ],
 },
 {
 icon: Mail,
 title: "Contacto",
 blocks: [
 { kind: "p", text: "Para qualquer questão relativa ao site ou ao seu conteúdo :" },
 { kind: "kv", rows: [
 ["Correio electrónico", "hello@nanda-sa.com"],
 ["Site", "www.nanda-sa.com"],
 ]},
 ],
 },
 ],
 },
};

// Confidentialité et cookies, pages courtes (structure body[])
const SHORT_CONTENT = {
 confidentialite: {
 fr: { label: "Légal", title: "Politique de confidentialité", body: [
 "Nous respectons votre vie privée. Les données collectées via nos formulaires sont utilisées uniquement pour répondre à votre demande.",
 "Aucune donnée n'est revendue à des tiers. Vous pouvez demander la suppression de vos données à tout moment en nous écrivant à hello@nanda-sa.com.",
 "Vous disposez d'un droit d'accès, de rectification et d'opposition sur vos données personnelles.",
 ]},
 en: { label: "Legal", title: "Privacy policy", body: [
 "We respect your privacy. Data collected via our forms is used solely to respond to your enquiry.",
 "No data is sold to third parties. You may request the deletion of your data at any time by writing to hello@nanda-sa.com.",
 "You have a right of access, rectification and opposition regarding your personal data.",
 ]},
 pt: { label: "Legal", title: "Política de privacidade", body: [
 "Respeitamos a sua privacidade. Os dados recolhidos através dos nossos formulários são utilizados apenas para responder ao seu pedido.",
 "Nenhum dado é vendido a terceiros. Pode solicitar a eliminação dos seus dados a qualquer momento escrevendo para hello@nanda-sa.com.",
 "Dispõe de um direito de acesso, rectificação e oposição sobre os seus dados pessoais.",
 ]},
 },
 cookies: {
 fr: { label: "Légal", title: "Politique cookies", body: [
 "Notre site utilise des cookies essentiels au fonctionnement et des cookies d'analyse anonymisés.",
 "Vous pouvez désactiver les cookies via les paramètres de votre navigateur.",
 ]},
 en: { label: "Legal", title: "Cookies policy", body: [
 "Our website uses cookies essential to its operation and anonymised analytics cookies.",
 "You may disable cookies through your browser settings.",
 ]},
 pt: { label: "Legal", title: "Política de cookies", body: [
 "O nosso website utiliza cookies essenciais ao funcionamento e cookies de análise anonimizados.",
 "Pode desactivar os cookies através das definições do seu navegador.",
 ]},
 },
};

// -----------------------------------------------------------------------------
// Render helpers
// -----------------------------------------------------------------------------

const Block = ({ block }) => {
 if (block.kind === "p") return <p className="text-nanda-inkSoft text-[15px] md:text-[16px] leading-[1.8]">{block.text}</p>;
 if (block.kind === "ul") return (
 <ul className="space-y-2.5 text-nanda-inkSoft text-[15px] md:text-[16px] leading-[1.7]">
 {block.items.map((it, i) => (
 <li key={i} className="flex items-start gap-3">
 <span className="w-1 h-1 rounded-full bg-nanda-terracotta mt-3 shrink-0" />
 <span>{it}</span>
 </li>
 ))}
 </ul>
 );
 if (block.kind === "kv") return (
 <dl className="grid grid-cols-1 sm:grid-cols-[minmax(0,180px)_1fr] gap-x-8 gap-y-3 text-[15px]">
 {block.rows.map(([k, v]) => (
 <React.Fragment key={k}>
 <dt className="label-overline text-nanda-muted pt-1">{k}</dt>
 <dd className="text-nanda-ink">{v}</dd>
 </React.Fragment>
 ))}
 </dl>
 );
 if (block.kind === "note") return (
 <div className="mt-2 text-xs md:text-sm italic text-nanda-muted border-l-2 border-nanda-line pl-4 py-1 leading-relaxed">
 {block.text}
 </div>
 );
 return null;
};

const Section = ({ section, index }) => {
 const Icon = section.icon;
 return (
 <StaggerItem>
 <section
 data-testid={`legal-section-${index + 1}`}
 className="grid grid-cols-1 md:grid-cols-[64px_1fr] gap-6 md:gap-10 py-12 md:py-16 border-t border-nanda-line"
 >
 <div className="flex md:justify-center md:pt-2">
 <div className="w-14 h-14 border border-nanda-line flex items-center justify-center bg-white">
 <Icon size={22} strokeWidth={1.3} className="text-nanda-terracotta" />
 </div>
 </div>
 <div className="max-w-[720px]">
 <div className="label-overline text-nanda-muted mb-3 tabular-nums">
 {String(index + 1).padStart(2, "0")}
 </div>
 <h2 className="font-serif text-2xl md:text-3xl lg:text-4xl text-nanda-ink leading-[1.15] mb-6">
 {section.title}
 </h2>
 <div className="space-y-5">
 {section.blocks.map((b, i) => (
 <Block key={i} block={b} />
 ))}
 </div>
 </div>
 </section>
 </StaggerItem>
 );
};

// -----------------------------------------------------------------------------
// Page component
// -----------------------------------------------------------------------------

export default function Legal() {
 const { type } = useParams();
 const { locale } = useLanguage();

 // Cas 1. Mentions légales (page riche sectionnée)
 if (!type || type === "mentions") {
 const c = MENTIONS_CONTENT[locale] || MENTIONS_CONTENT.fr;
 return (
 <div data-testid="page-legal-mentions" className="bg-nanda-bg">
 {/* HERO */}
 <section className="border-b border-nanda-line">
 <div className="mx-auto max-w-[1100px] px-6 md:px-10 lg:px-14 pt-24 md:pt-40 pb-14 md:pb-20">
 <FadeUp>
 <div className="label-overline text-nanda-muted mb-6">{c.label}</div>
 <h1 className="font-serif text-5xl md:text-7xl lg:text-[84px] leading-[1.02]">{c.title}</h1>
 <div className="mt-4 text-xs md:text-sm text-nanda-muted label-overline">{c.updatedAt}</div>
 </FadeUp>
 <FadeUp delay={0.1} className="mt-12 md:mt-16 max-w-3xl">
 <p className="text-nanda-inkSoft text-[17px] md:text-lg leading-[1.75]">{c.intro}</p>
 </FadeUp>
 </div>
 </section>

 {/* SECTIONS */}
 <section className="mx-auto max-w-[1100px] px-6 md:px-10 lg:px-14 py-10 md:py-16">
 <Stagger>
 {c.sections.map((s, i) => (
 <Section key={i} section={s} index={i} />
 ))}
 </Stagger>
 </section>
 </div>
 );
 }

 // Cas 2. Politique de confidentialité / cookies (page courte)
 const entry = SHORT_CONTENT[type] || SHORT_CONTENT.confidentialite;
 const c = entry[locale] || entry.fr;
 return (
 <div data-testid={`page-legal-${type}`}>
 <section className="mx-auto max-w-[900px] px-6 md:px-10 lg:px-14 pt-24 md:pt-40 pb-24 md:pb-32">
 <FadeUp>
 <div className="label-overline text-nanda-muted mb-6">{c.label}</div>
 <h1 className="font-serif text-5xl md:text-7xl leading-[1.0]">{c.title}</h1>
 </FadeUp>
 <FadeUp delay={0.1} className="mt-16 space-y-6 text-nanda-inkSoft text-base md:text-[17px] leading-relaxed">
 {c.body.map((p, i) => <p key={i}>{p}</p>)}
 </FadeUp>
 </section>
 </div>
 );
}
